import { useEffect, useState } from 'react';
import api from '@/api';
import { BarChart2, TrendingUp, Package, Truck } from 'lucide-react';

export default function CRM() {
  const [customers, setCustomers] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/customers'),
      api.get('/admin/rfqs'),
    ]).then(([cr, rr]) => {
      setCustomers(cr.data);
      setRfqs(rr.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const getRfqCount = (cid) => rfqs.filter(r => r.customer_id === cid).length;
  const getLastActivity = (cid) => {
    const r = rfqs.filter(r => r.customer_id === cid).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    return r ? new Date(r.created_at).toLocaleDateString() : 'No activity';
  };

  return (
    <div className="space-y-5" data-testid="admin-crm-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">CRM Tracking</h1>
        <p className="text-slate-500 text-sm mt-0.5">Customer activity and engagement overview</p>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-white rounded-xl border animate-pulse" />)}</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full" data-testid="crm-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Platforms</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Total RFQs</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Last Activity</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {customers.map(c => (
                <tr key={c._id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="text-sm font-semibold text-slate-900">{c.name}</p>
                    <p className="text-xs text-slate-400">{c.company_name || c.email}</p>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex gap-1">
                      {c.platform_access?.demaxtore && <span className="text-xs bg-sky-50 text-sky-700 font-semibold px-1.5 py-0.5 rounded">DM</span>}
                      {c.platform_access?.commoditybid && <span className="text-xs bg-orange-50 text-orange-700 font-semibold px-1.5 py-0.5 rounded">CB</span>}
                      {c.platform_access?.freightiq && <span className="text-xs bg-green-50 text-green-700 font-semibold px-1.5 py-0.5 rounded">FQ</span>}
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden sm:table-cell">
                    <span className="text-sm font-semibold text-slate-900">{getRfqCount(c._id)}</span>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell text-sm text-slate-500">{getLastActivity(c._id)}</td>
                  <td className="px-5 py-4 hidden lg:table-cell">
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${c.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
